/**
 * Integrytas Group - Document Generation (DOC)
 * Page: DOC.TEMPLATES
 *
 * Setup interface: author document templates (primary entity, localized rich-HTML
 * body with {{tokens}} and {{#section}} blocks), related-data bindings, and a
 * test-generate action. Generation runs through DocService (RLS-scoped fetch →
 * merge → optional PDF → DMS).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { supabase } from '../../lib/supabase_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { generateDocument } from '../../services/doc/DocService';
import { Modal } from '../../components/common/Modal';
import { PdfViewer } from '../../components/common/PdfViewer';
import { getSignedUrl } from '../../services/dms/DmsService';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { useFormatDateTime } from '../../lib/format_date';
import { useActiveLocale } from '../../lib/use_active_locale';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search, Maximize2, Minimize2 } from 'lucide-react';

const PAGE_CODE = 'DOC.TEMPLATES';

registerUdcHost('DOC.TEMPLATES', 'doc_template', 'Document Templates');

const LOCALES = ['EN_US', 'ES_MX'];

const WORKER_URL = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/doc-worker`;

/** How often to ask whether a run has finished, and when to stop waiting. */
const RUN_POLL_MS = 1200;
const RUN_TIMEOUT_MS = 180000;

// ── CHECK-enum labelled constant (§17.B11) — stored value is the code; the label
// is localized and is the ONLY thing rendered in cells/facets/options.
interface EnumOpt { value: string; labelKey: string; fallback: string; }

const TEMPLATE_KINDS: EnumOpt[] = [
  { value: 'MAILMERGE',     labelKey: 'doc.template_kind.MAILMERGE',     fallback: 'Mail Merge' },
  { value: 'BANDED_REPORT', labelKey: 'doc.template_kind.BANDED_REPORT', fallback: 'Banded Report' },
];

// Per-kind valid output formats (mirrors the doc_template_output_check DB rule).
const OUTPUT_BY_KIND: Record<string, EnumOpt[]> = {
  MAILMERGE: [
    { value: 'HTML', labelKey: 'doc.output_format.HTML', fallback: 'HTML' },
    { value: 'PDF',  labelKey: 'doc.output_format.PDF',  fallback: 'PDF' },
  ],
  BANDED_REPORT: [
    { value: 'PDF',  labelKey: 'doc.output_format.PDF',  fallback: 'PDF' },
    { value: 'XLSX', labelKey: 'doc.output_format.XLSX', fallback: 'Excel (XLSX)' },
    { value: 'CSV',  labelKey: 'doc.output_format.CSV',  fallback: 'CSV' },
  ],
};

const TEMPLATE_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 1 },
  { key: 'name', type: 'text', label: 'Name', col_span: 2 },
  { key: 'entity', type: 'text', label: 'Primary Entity', required: true, col_span: 1 },
  { key: 'pk', type: 'text', label: 'PK Column', required: true, col_span: 1 },
  { key: 'output', type: 'select', label: 'Default Output', col_span: 1 },
];
const TEMPLATE_CORE_LAYOUT = toUldCoreLayout(TEMPLATE_FIELD_DEFS);

const TEMPLATE_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',  header: 'Code',   defaultWidth: 190 },
  { key: 'name',           header: 'Name',   defaultWidth: 200 },
  { key: 'template_kind',  header: 'Kind',   defaultWidth: 130 },
  { key: 'primary_entity', header: 'Entity', defaultWidth: 180 },
  { key: 'output_default', header: 'Output', defaultWidth: 100 },
  { key: 'is_active',      header: 'Active', defaultWidth: 80  },
];

interface TemplateRow {
  doc_template_id: number; internal_code: string; name: string; primary_entity: string | null;
  pk_column: string | null; template_kind: string; output_default: 'HTML' | 'PDF' | 'XLSX' | 'CSV';
  is_active: 'Y' | 'N'; description: string | null;
}
interface TlRow { locale_code: string; title: string | null; body_html: string | null }
interface BindingRow { doc_binding_id: number; binding_name: string; related_entity: string; fk_column: string; order_by: string | null }

export interface DocTemplateManagerPageProps { session: AuthSession }

export function DocTemplateManagerPage({ session }: DocTemplateManagerPageProps) {
  return (
    <PersonalizationProvider pageCode="DOC.TEMPLATES" session={session}>
      <DocTemplateManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DocTemplateManagerPageInner({ session }: DocTemplateManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TemplateRow | null>(null);
  const [runTarget, setRunTarget] = useState<TemplateRow | null>(null);
  const [versionsTarget, setVersionsTarget] = useState<TemplateRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ kind: string[]; entity: string[]; output: string[]; active: string[] }>(
    { kind: [], entity: [], output: [], active: ['Y'] },
  );
  const udcGrid = useUdcGrid('doc_template', 'DOC.TEMPLATES', { pkColumn: 'doc_template_id', rows: rows as unknown as Record<string, unknown>[] });

  // §17.B13: related-children expand/contract. Template bindings/versions are
  // fetched lazily inside the editor modal, not at list level, so the list-row
  // disclosure renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const kindLabel = (v: string) => {
    const o = TEMPLATE_KINDS.find(k => k.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('doc_template')
        .select('doc_template_id, internal_code, name, template_kind, primary_entity, pk_column, output_default, is_active, description').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as TemplateRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const handleDelete = async (row: TemplateRow) => {
    const { error: e } = await routedDelete(session, 'doc_template', 'doc_template_id', row.doc_template_id);
    if (e) { setError(e.message); return; }
    setDeleteTarget(null);
    void reload();
  };

  // Export a template's portable definition JSON and trigger a browser download.
  const handleExport = async (row: TemplateRow) => {
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; document?: unknown; error_code?: string }>(
        'doc_export_definition', { p_template_id: row.doc_template_id });
      if (e) throw new Error(e.message);
      const res = data as { ok: boolean; document?: unknown; error_code?: string } | null;
      if (!res?.ok || !res.document) throw new Error(res?.error_code ?? t('doc.err.export_failed', 'Export failed.'));
      const blob = new Blob([JSON.stringify(res.document, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.internal_code}.doc-report.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError((e as Error).message); }
  };

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: (string | null)[]) => [...new Set(vals.filter((v): v is string => !!v))].sort();
    return {
      kind: TEMPLATE_KINDS.map(k => ({ value: k.value, label: t(k.labelKey, k.fallback) })),
      entity: uniq(rows.map(r => r.primary_entity)).map(v => ({ value: v, label: v })),
      output: uniq(rows.map(r => r.output_default)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.kind.length + facets.entity.length + facets.output.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ kind: [], entity: [], output: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // OR within a facet, AND across facets.
    const matchesFacets = (r: TemplateRow) =>
      (facets.kind.length === 0 || facets.kind.includes(r.template_kind)) &&
      (facets.entity.length === 0 || (r.primary_entity != null && facets.entity.includes(r.primary_entity))) &&
      (facets.output.length === 0 || facets.output.includes(r.output_default)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    return rows.filter(r =>
      (!q ||
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.primary_entity ?? '').toLowerCase().includes(q))
      && udcGrid.matchRow(r as unknown as Record<string, unknown>)
      && matchesFacets(r));
  }, [rows, filter, facets, udcGrid]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('doc.title', 'Document Templates')}
          description={t('doc.list.description', 'Author document templates, related bindings and output formats, then test-generate or run them.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('doc.refresh', 'Refresh')}
              </button>
              <button type="button" onClick={() => setImportOpen(true)} className="btn-secondary flex items-center gap-1.5">
                {t('doc.import_lbl', 'Import')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('doc.new_template', 'New Template')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('doc.stat.total', 'Total templates') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('doc.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('doc.error_banner', "Couldn't complete the last document-template operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('doc.filter_placeholder', 'Filter templates…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('doc.col.kind', 'Kind')} options={facetOptions.kind} selected={facets.kind} onChange={v => setFacets(f => ({ ...f, kind: v }))} />
            <FacetFilter label={t('doc.col.entity', 'Entity')} options={facetOptions.entity} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <FacetFilter label={t('doc.col.output', 'Output')} options={facetOptions.output} selected={facets.output} onChange={v => setFacets(f => ({ ...f, output: v }))} />
            <FacetFilter label={t('doc.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('doc.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...TEMPLATE_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('doc.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('doc.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.doc_template_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.doc_template_id)}
                        hasChildren={hasRelated(r.doc_template_id)}
                        onToggle={() => detail.toggle(r.doc_template_id)}
                        expandLabel={t('doc.related.expand', 'Show related records')}
                        collapseLabel={t('doc.related.collapse', 'Hide related')}
                        tooltip={t('doc.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code'  && <span className="font-mono">{r.internal_code}</span>}
                        {col.key === 'name'           && r.name}
                        {col.key === 'template_kind'  && kindLabel(r.template_kind)}
                        {col.key === 'primary_entity' && (r.primary_entity ? <span className="font-mono text-csm-xs">{r.primary_entity}</span> : <span className="text-csm-2xs text-csm-muted">—</span>)}
                        {col.key === 'output_default' && <span className="text-csm-xs">{r.output_default}</span>}
                        {col.key === 'is_active'      && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)    && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                      <button onClick={e => { e.stopPropagation(); setRunTarget(r); }} className="mr-1 rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('doc.action.run', 'Run')}</button>
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('doc.action.edit', 'Edit')}</button>)}
                      <button onClick={e => { e.stopPropagation(); void handleExport(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('doc.action.export', 'Export')}</button>
                      <button onClick={e => { e.stopPropagation(); setVersionsTarget(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('doc.action.versions', 'Versions')}</button>
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('doc.action.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.doc_template_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.doc_template_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('doc.empty', 'No templates yet')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('doc.new_template_plus', '+ New Template')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <TemplateModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
      {runTarget && (
        <RunReportModal template={runTarget} onClose={() => setRunTarget(null)} />
      )}
      {importOpen && (
        <ImportDefinitionModal onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); void reload(); }} />
      )}
      {versionsTarget && (
        <VersionsModal template={versionsTarget} onClose={() => setVersionsTarget(null)} onChanged={() => void reload()} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={t('doc.delete.title', 'Delete Template?')}
          message={t('doc.delete.message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  RUN REPORT — output-format + params prompt → doc_run_report + fire worker   */
/* ══════════════════════════════════════════════════════════════════════════ */

interface ParamPair { key: string; value: string }

/** A parameter the report DECLARES (doc_report_param + its localized prompt). */
interface DeclaredParam {
  param_code: string;
  data_type: 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'LOOKUP';
  lookup_source: string | null;
  default_expr: string | null;
  is_required: 'Y' | 'N';
  prompt: string;
}

interface LookupOption { value: string; label: string }

/**
 * Load a report's declared parameters, and for every LOOKUP one the pick list
 * behind it. The picker is what keeps §17.B4 true at run time: the user chooses
 * "Jane Doe — jane@…", never types the surrogate id the filter actually binds.
 *
 * Lookup datasets follow one convention: the first exposed column (by
 * sort_order) is the VALUE, the second is the LABEL.
 */
function useDeclaredParams(templateId: number, locale: string) {
  const [params, setParams] = useState<DeclaredParam[] | null>(null);
  const [options, setOptions] = useState<Record<string, LookupOption[]>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: e } = await apiDb
          .from('doc_report_param')
          .select(`
            doc_report_param_id, param_code, data_type, lookup_source, default_expr, is_required, sort_order,
            doc_report_param_tl ( locale_code, display_text )
          `)
          .eq('doc_template_id', templateId)
          .order('sort_order');
        if (e) throw e;

        type RawParam = {
          param_code: string; data_type: DeclaredParam['data_type'];
          lookup_source: string | null; default_expr: string | null; is_required: 'Y' | 'N';
          doc_report_param_tl?: { locale_code: string; display_text: string }[];
        };
        const declared: DeclaredParam[] = ((data ?? []) as RawParam[]).map(p => {
          const tls = p.doc_report_param_tl ?? [];
          const exact = tls.find(t => t.locale_code === locale);
          const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
          return {
            param_code: p.param_code,
            data_type: p.data_type,
            lookup_source: p.lookup_source,
            default_expr: p.default_expr,
            is_required: p.is_required,
            prompt: exact?.display_text ?? fallback?.display_text ?? p.param_code,
          };
        });
        if (cancelled) return;
        setParams(declared);

        // One cds_run per distinct lookup source; a failing picker must not stop
        // the run dialog opening, so each is best-effort.
        const sources = Array.from(new Set(
          declared.map(p => p.lookup_source).filter((s): s is string => !!s)));
        const loaded: Record<string, LookupOption[]> = {};
        await Promise.all(sources.map(async source => {
          try {
            const { data: res } = await apiDb.rpc<{ ok: boolean; columns?: { key: string }[]; rows?: Record<string, unknown>[] }>(
              'cds_run', { p_dataset_code: source, p_params: {} });
            const wrapper = res as { ok?: boolean; columns?: { key: string }[]; rows?: Record<string, unknown>[] } | null;
            if (!wrapper?.ok || !wrapper.columns?.length) return;
            const valueKey = wrapper.columns[0].key;
            const labelKey = wrapper.columns[1]?.key ?? valueKey;
            loaded[source] = (wrapper.rows ?? []).map(r => ({
              value: String(r[valueKey] ?? ''),
              label: String(r[labelKey] ?? r[valueKey] ?? ''),
            })).filter(o => o.value !== '');
          } catch { /* picker unavailable — the field stays a plain input */ }
        }));
        if (!cancelled) setOptions(loaded);
      } catch (e) {
        if (!cancelled) { setParams([]); setError((e as Error).message); }
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, locale]);

  return { params, options, error };
}

function RunReportModal({ template, onClose }: { template: TemplateRow; onClose: () => void }) {
  const { t } = useTranslation();
  const locale = useActiveLocale();
  const outputOpts = OUTPUT_BY_KIND[template.template_kind] ?? OUTPUT_BY_KIND.BANDED_REPORT;
  const [format, setFormat] = useState(template.output_default && outputOpts.some(o => o.value === template.output_default)
    ? template.output_default : outputOpts[0].value);
  const [params, setParams] = useState<ParamPair[]>([{ key: '', value: '' }]);
  const [busy, setBusy] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const declared = useDeclaredParams(template.doc_template_id, locale);
  const [values, setValues] = useState<Record<string, string>>({});

  // Seed each declared parameter from its default once the declaration lands.
  useEffect(() => {
    if (!declared.params) return;
    setValues(prev => {
      const next = { ...prev };
      for (const p of declared.params ?? []) {
        if (next[p.param_code] === undefined) next[p.param_code] = p.default_expr ?? '';
      }
      return next;
    });
  }, [declared.params]);

  const usesDeclared = (declared.params?.length ?? 0) > 0;
  const missingRequired = (declared.params ?? [])
    .filter(p => p.is_required === 'Y' && !(values[p.param_code] ?? '').trim())
    .map(p => p.prompt);

  const setPair = (i: number, patch: Partial<ParamPair>) =>
    setParams(ps => ps.map((p, j) => j === i ? { ...p, ...patch } : p));

  /**
   * Poll the run to completion.
   *
   * Firing the worker and closing the dialog left the reader with nothing at
   * all: no sign the report had started, and no sign when it finished — the
   * artifact only appeared on a different screen, after a manual Refresh. So the
   * dialog now stays put and reports progress, then hands back the finished
   * document. The pg_cron sweep is still the backstop if this page is closed.
   */
  const awaitRender = async (docGeneratedId: number): Promise<string> => {
    const deadline = Date.now() + RUN_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, RUN_POLL_MS));
      const { data, error: e } = await apiDb
        .from('doc_generated')
        .select('status, error, dms_document_id')
        .eq('doc_generated_id', docGeneratedId)
        .maybeSingle();
      if (e) throw new Error(e.message);
      const row = data as { status?: string; error?: string | null; dms_document_id?: number | null } | null;
      if (row?.status === 'FAILED') throw new Error(row.error || t('doc.run.failed', 'The report failed to render.'));
      if (row?.status === 'RENDERED') {
        if (row.dms_document_id == null) throw new Error(t('doc.run.no_artifact', 'The report rendered but produced no file.'));
        const { data: docRow } = await apiDb
          .from('dms_document').select('storage_path').eq('document_id', row.dms_document_id).maybeSingle();
        const path = (docRow as { storage_path?: string } | null)?.storage_path;
        if (!path) throw new Error(t('doc.run.no_artifact', 'The report rendered but produced no file.'));
        return getSignedUrl(path);
      }
    }
    throw new Error(t('doc.run.timeout', 'The report is taking longer than expected. It will appear in Generated Documents when it finishes.'));
  };

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const paramObj: Record<string, string> = {};
      if (usesDeclared) {
        for (const p of declared.params ?? []) {
          const v = (values[p.param_code] ?? '').trim();
          if (v) paramObj[p.param_code] = v;
        }
      } else {
        for (const p of params) { if (p.key.trim()) paramObj[p.key.trim()] = p.value; }
      }
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; doc_generated_id?: number; task_id?: number; error_code?: string }>(
        'doc_run_report', {
          p_template_code: template.internal_code,
          p_params: paramObj,
          p_output_format: format,
          p_locale: locale,
          p_title: runTitle(),
        });
      if (e) throw new Error(e.message);
      const res = data as { ok: boolean; task_id?: number; doc_generated_id?: number; error_code?: string } | null;
      if (!res?.ok) throw new Error(res?.error_code ?? t('doc.err.run_failed', 'Report run could not be started.'));

      // Fire-and-forget the worker (mirrors use_task_queue.ts).
      if (res.task_id != null) {
        const { data: sd } = await supabase.auth.getSession();
        const token = sd.session?.access_token ?? '';
        void fetch(WORKER_URL, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ task_id: res.task_id }),
        }).catch(() => { /* fire-and-forget; the pg_cron sweep is the fallback */ });
      }

      if (res.doc_generated_id == null) { onClose(); return; }
      setRendering(true);
      setResultUrl(await awaitRender(res.doc_generated_id));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  /**
   * What this run was FOR, for the Generated Documents queue — the report name
   * alone just repeats the Template column next to it.
   *
   * Only LOOKUP parameters contribute: their chosen option carries a resolved
   * label ("Alice Chen — alice.chen@…"), and it is the subject of the report.
   * A scalar filter's raw value would read as noise ("N"), and only the caller
   * can resolve a lookup id at all — the label lives in whichever dataset the
   * parameter names, which is why the server takes this as an override rather
   * than deriving it. Empty here means the server falls back to the localized
   * template title, never NULL.
   */
  const runTitle = (): string | null => {
    const subjects = (declared.params ?? [])
      .filter(p => p.data_type === 'LOOKUP')
      .map(p => {
        const value = (values[p.param_code] ?? '').trim();
        if (!value || !p.lookup_source) return null;
        return declared.options[p.lookup_source]?.find(o => o.value === value)?.label ?? null;
      })
      .filter((label): label is string => !!label);
    if (subjects.length === 0) return null;
    return `${template.name}: ${subjects.join(', ')}`.slice(0, 300);
  };

  const control = (p: DeclaredParam) => {
    const value = values[p.param_code] ?? '';
    const set = (v: string) => setValues(prev => ({ ...prev, [p.param_code]: v }));
    const cls = 'h-8 w-full rounded-csm-control border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none';

    if (p.data_type === 'LOOKUP') {
      const opts = p.lookup_source ? declared.options[p.lookup_source] : undefined;
      if (!opts) {
        // The pick list could not be resolved — fall back to a plain field rather
        // than offering an empty dropdown the user cannot get past.
        return <input value={value} onChange={e => set(e.target.value)} className={cls} />;
      }
      return (
        <select value={value} onChange={e => set(e.target.value)} className={cls}>
          <option value="">{p.is_required === 'Y'
            ? t('doc.run.choose', 'Choose…')
            : t('doc.run.any', 'All')}</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (p.data_type === 'BOOLEAN') {
      return (
        <select value={value} onChange={e => set(e.target.value)} className={cls}>
          <option value="">{t('doc.run.any', 'All')}</option>
          <option value="Y">{t('common.boolean.yes', 'Yes')}</option>
          <option value="N">{t('common.boolean.no', 'No')}</option>
        </select>
      );
    }
    const inputType = p.data_type === 'DATE' ? 'date' : p.data_type === 'NUMBER' ? 'number' : 'text';
    return <input type={inputType} value={value} onChange={e => set(e.target.value)} className={cls} />;
  };

  return (
    <Modal
      title={t('doc.run.title', 'Run Report — {{code}}', { code: template.internal_code })}
      onClose={onClose}
      width={resultUrl ? (expanded ? 'full' : '4xl') : 'lg'}
      headerAction={resultUrl ? (
        <button type="button" onClick={() => setExpanded(e => !e)}
          className="rounded-csm-control border border-csm-border bg-white px-2 py-1 text-csm-xs hover:bg-csm-surface-alt"
          title={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}>
          {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      ) : undefined}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
            {resultUrl ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
          </button>
          {!resultUrl && (
            <button onClick={() => void run()} disabled={busy || missingRequired.length > 0}
              title={missingRequired.length > 0 ? missingRequired.join(', ') : undefined}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('doc.run.starting', 'Starting…') : t('doc.run.start', 'Run')}</button>
          )}
        </>
      }
    >
      {/* Finished: the document itself, in the dialog the run was started from. */}
      {resultUrl && (
        <PdfViewer url={resultUrl} fileName={`${template.name} (${template.internal_code}).pdf`}
          width={expanded ? 1200 : 820} maxHeight={expanded ? '78vh' : '68vh'} />
      )}

      {/* Running: the reader gets a live signal instead of a closed dialog. */}
      {!resultUrl && rendering && (
        <div className="py-10 text-center">
          <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-csm-border border-t-primary" />
          <div className="text-csm-sm text-csm-ink-soft">{t('doc.run.rendering', 'Generating the report…')}</div>
          <div className="mt-1 text-csm-xs text-csm-muted">{t('doc.run.rendering_hint', 'It will open here when it is ready, and is saved to Generated Documents either way.')}</div>
        </div>
      )}

      {!resultUrl && !rendering && (<>
      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.run.output_format', 'Output Format')}</label>
      <select value={format} onChange={e => setFormat(e.target.value)} className="mb-4 h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
        {outputOpts.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
      </select>

      {/* A report that declares its parameters prompts for them by name; anything
          else keeps the free-form key/value editor. */}
      {usesDeclared ? (
        <>
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.run.params', 'Parameters')}</div>
          {(declared.params ?? []).map(p => (
            <div key={p.param_code} className="mb-3">
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
                {p.prompt}
                {p.is_required === 'Y' && <span className="ml-1 text-csm-danger">*</span>}
              </label>
              {control(p)}
            </div>
          ))}
        </>
      ) : (
        <>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.run.params', 'Parameters')}</span>
            <button onClick={() => setParams(ps => [...ps, { key: '', value: '' }])} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">+ {t('doc.run.add_param', 'Parameter')}</button>
          </div>
          {params.map((p, i) => (
            <div key={i} className="mb-1 flex items-center gap-1">
              <input value={p.key} onChange={e => setPair(i, { key: e.target.value })} placeholder={t('doc.run.param_key', 'name')} className="h-7 w-40 rounded border border-csm-border px-2 font-mono text-csm-xs" />
              <input value={p.value} onChange={e => setPair(i, { value: e.target.value })} placeholder={t('doc.run.param_value', 'value')} className="h-7 flex-1 rounded border border-csm-border px-2 text-csm-xs" />
              <button onClick={() => setParams(ps => ps.filter((_, j) => j !== i))} className="text-csm-danger text-csm-xs px-1">{t('doc.run.remove_param', 'remove')}</button>
            </div>
          ))}
        </>
      )}

      </>)}

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  IMPORT DEFINITION — file input + collision strategy → doc_import_definition */
/* ══════════════════════════════════════════════════════════════════════════ */

function ImportDefinitionModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [payload, setPayload] = useState<unknown>(null);
  const [fileName, setFileName] = useState('');
  const [collision, setCollision] = useState<'CLONE' | 'VERSION'>('CLONE');
  const [newCode, setNewCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (f: File | undefined) => {
    setError(null); setPayload(null); setFileName('');
    if (!f) return;
    try {
      const text = await f.text();
      setPayload(JSON.parse(text));
      setFileName(f.name);
    } catch {
      setError(t('doc.import.err.parse', 'That file is not valid JSON.'));
    }
  };

  const doImport = async () => {
    setBusy(true); setError(null);
    try {
      if (payload == null) throw new Error(t('doc.import.err.no_file', 'Choose a definition file to import.'));
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'doc_import_definition', {
          p_payload: payload,
          p_collision: collision,
          p_new_code: collision === 'CLONE' && newCode.trim() ? newCode.trim().toUpperCase() : null,
        });
      if (e) throw new Error(e.message);
      const res = data as { ok: boolean; error_code?: string } | null;
      if (!res?.ok) throw new Error(res?.error_code ?? t('doc.import.err.failed', 'Import failed.'));
      onImported();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('doc.import.title', 'Import Report Definition')}
      onClose={onClose}
      width="lg"
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void doImport()} disabled={busy || payload == null} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('doc.import.importing', 'Importing…') : t('doc.import.action', 'Import')}</button>
        </>
      }
    >
      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.import.file', 'Definition File (.json)')}</label>
      <input ref={fileRef} type="file" accept=".json,application/json" onChange={e => void onFile(e.target.files?.[0])}
        className="mb-1 block w-full text-csm-sm" />
      {fileName && <p className="mb-3 text-csm-xs text-csm-ink-soft">{fileName}</p>}

      <div className="mb-3">
        <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.import.collision', 'If the code already exists')}</span>
        <label className="mb-1 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="radio" name="collision" checked={collision === 'CLONE'} onChange={() => setCollision('CLONE')} className="h-4 w-4 accent-gray-700" />
          {t('doc.import.clone', 'Clone as a new template')}
        </label>
        <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="radio" name="collision" checked={collision === 'VERSION'} onChange={() => setCollision('VERSION')} className="h-4 w-4 accent-gray-700" />
          {t('doc.import.version', 'Add as a new (inactive) version of the existing template')}
        </label>
      </div>

      {collision === 'CLONE' && (
        <>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.import.new_code', 'New Code (optional)')}</label>
          <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} placeholder={t('doc.import.new_code_hint', 'Leave blank to auto-suffix')} className="mb-3 h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
        </>
      )}

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  VERSIONS — history list with Activate / Roll back → doc_activate_version     */
/* ══════════════════════════════════════════════════════════════════════════ */

interface VersionRow {
  doc_template_version_id: number;
  version_no: number;
  is_current: 'Y' | 'N';
  note: string | null;
  created_at: string;
}

function VersionsModal({ template, onClose, onChanged }: { template: TemplateRow; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<VersionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('doc_template_version')
        .select('doc_template_version_id, version_no, is_current, note, created_at')
        .eq('doc_template_id', template.doc_template_id)
        .order('version_no', { ascending: false });
      if (e) throw e;
      setRows((data ?? []) as VersionRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }, [template.doc_template_id]);
  useEffect(() => { void reload(); }, [reload]);

  const activate = async (v: VersionRow) => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'doc_activate_version', { p_version_id: v.doc_template_version_id });
      if (e) throw new Error(e.message);
      const res = data as { ok: boolean; error_code?: string } | null;
      if (!res?.ok) throw new Error(res?.error_code ?? t('doc.versions.err.activate', 'Could not activate that version.'));
      await reload();
      onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('doc.versions.title', 'Version History — {{code}}', { code: template.internal_code })}
      onClose={onClose}
      width="2xl"
      footer={<button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('doc.close', 'Close')}</button>}
    >
      <table className={GRID_TABLE_CLASS}>
        <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
          <tr>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('doc.versions.col.version', 'Version')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('doc.versions.col.current', 'Current')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('doc.versions.col.note', 'Note')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('doc.versions.col.created', 'Created')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(v => (
            <tr key={v.doc_template_version_id} className="hover:bg-csm-surface-alt">
              <td className="border-b border-csm-border-soft px-2 py-1 text-csm-sm font-mono">v{v.version_no}</td>
              <td className="border-b border-csm-border-soft px-2 py-1"><YesNoBadge value={v.is_current} yesLabel={t('doc.versions.current', 'Current')} noLabel={t('doc.versions.not_current', '—')} /></td>
              <td className="border-b border-csm-border-soft px-2 py-1 text-csm-sm">{v.note || <span className="text-csm-2xs text-csm-muted">—</span>}</td>
              <td className="border-b border-csm-border-soft px-2 py-1 text-csm-sm">{formatDateTime(v.created_at)}</td>
              <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                {v.is_current === 'Y'
                  ? <span className="text-csm-2xs text-csm-muted">{t('doc.versions.active', 'Active')}</span>
                  : <button onClick={() => void activate(v)} disabled={busy} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover disabled:opacity-60">{t('doc.versions.activate', 'Activate / Roll back')}</button>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && !busy && (
            <tr><td colSpan={5} className="py-8 text-center"><EmptyState title={t('doc.versions.empty', 'No saved versions yet')} /></td></tr>
          )}
        </tbody>
      </table>
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

function TemplateModal({ session, row, onClose, onSaved }: { session: AuthSession; row: TemplateRow | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const actor = getForensicActorId(session);
  const id = row?.doc_template_id;

  const kind = row?.template_kind ?? 'MAILMERGE';
  const outputOpts = OUTPUT_BY_KIND[kind] ?? OUTPUT_BY_KIND.MAILMERGE;
  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    entity: row?.primary_entity ?? '',
    pk: row?.pk_column ?? '',
    output: (row?.output_default ?? outputOpts[0].value) as string,
    active: row ? row.is_active === 'Y' : false,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('doc_template', 'DOC.TEMPLATES', { pkColumn: 'doc_template_id', pk: row?.doc_template_id });
  const { layout } = useUldLayout('DOC.TEMPLATES.MODAL', useMemo(() => [...TEMPLATE_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]));
  const [tl, setTl] = useState<Record<string, TlRow>>({ EN_US: { locale_code: 'EN_US', title: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', title: '', body_html: '' } });
  const [bindings, setBindings] = useState<BindingRow[]>([]);
  const [testId, setTestId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const loadChildren = async () => {
    if (!id) return;
    const [t, b] = await Promise.all([
      apiDb.from('doc_template_tl').select('locale_code, title, body_html').eq('doc_template_id', id),
      apiDb.from('doc_binding').select('doc_binding_id, binding_name, related_entity, fk_column, order_by').eq('doc_template_id', id),
    ]);
    const next = { EN_US: { locale_code: 'EN_US', title: '', body_html: '' }, ES_MX: { locale_code: 'ES_MX', title: '', body_html: '' } } as Record<string, TlRow>;
    ((t.data ?? []) as TlRow[]).forEach((r) => { next[r.locale_code] = { locale_code: r.locale_code, title: r.title ?? '', body_html: r.body_html ?? '' }; });
    setTl(next); setBindings((b.data ?? []) as BindingRow[]);
  };

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    void loadChildren();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validateCode = (val: string) => {
    if (!val) { setInternalCodeError(null); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(val)) {
      setInternalCodeError(t('doc.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setInternalCodeError(null);
    }
  };

  const saveTemplate = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('doc.err.code_required', 'Code is required.'));
      if (internalCodeError) throw new Error(internalCodeError);
      // MAILMERGE needs a primary entity + PK column (DB CHECK); BANDED_REPORT has
      // neither — those are authored in the designer / arrive via import.
      const isMailMerge = kind === 'MAILMERGE';
      if (isMailMerge && (!form.entity.trim() || !form.pk.trim())) throw new Error(t('doc.err.entity_pk_required', 'Primary entity and PK column are required.'));
      const payload = {
        name: form.name.trim(),
        primary_entity: isMailMerge ? form.entity.trim() : null,
        pk_column: isMailMerge ? form.pk.trim() : null,
        output_default: form.output,
        is_active: form.active ? 'Y' : 'N',
      };
      let tid = id;
      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'doc_template', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw e;
        tid = row_id ?? undefined;
      } else {
        const { error: e } = await routedUpdate(session, 'doc_template', 'doc_template_id', id, payload); if (e) throw e;
      }
      const tlRows = LOCALES.map(l => tl[l]).filter(t => (t.title ?? '').trim() || (t.body_html ?? '').trim())
        .map(t => ({ doc_template_id: tid, locale_code: t.locale_code, title: t.title || null, body_html: t.body_html || null, modified_by: actor }));
      if (tlRows.length) { const { error: e } = await apiDb.from('doc_template_tl').upsert(tlRows, { onConflict: 'doc_template_id,locale_code' }); if (e) throw e; }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addBinding = async () => { if (!id) return; await routedInsert(session, 'doc_binding', { doc_template_id: id, binding_name: '', related_entity: '', fk_column: '' }); await loadChildren(); };
  const saveBinding = async (b: BindingRow) => { await routedUpdate(session, 'doc_binding', 'doc_binding_id', b.doc_binding_id, { binding_name: b.binding_name, related_entity: b.related_entity, fk_column: b.fk_column, order_by: b.order_by || null }); };
  const delBinding = async (bid: number) => { await routedDelete(session, 'doc_binding', 'doc_binding_id', bid); setBindings(bs => bs.filter(x => x.doc_binding_id !== bid)); };

  const testGenerate = async () => {
    if (!row || !testId.trim()) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await generateDocument(row.internal_code, testId.trim(), { session });
      setNotice(res.dmsDocumentId
        ? t('doc.notice.generated_stored', 'Generated {{format}} (stored as DMS #{{id}}).', { format: res.format, id: res.dmsDocumentId })
        : t('doc.notice.generated', 'Generated {{format}}.', { format: res.format }));
      if (res.format === 'HTML') {
        const w = window.open('', '_blank'); if (w) { w.document.write(res.html); w.document.close(); }
      }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <>
      <Modal
        title={isCreate ? t('doc.modal.create_title', 'Create Template') : t('doc.modal.edit_title', 'Edit Template — {{code}}', { code: row?.internal_code ?? '' })}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('doc.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
        <FieldRenderer
          fields={useMemo(() => [...TEMPLATE_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs])}
          layout={layout}
          columns={3}
          pageCode="DOC.TEMPLATES.MODAL"
          session={session}
          render={(fld) => {
            if (fld.key === 'code') return (
              <PersonalizedField componentKey="modal" fieldKey="code" label={t('doc.field.code', 'Code')}>
              <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.field.code', 'Code')}</label>
                <input type="text" value={form.code} disabled={!isCreate}
                       onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                       onBlur={e => validateCode(e.target.value)}
                       className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                {internalCodeError
                  ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                  : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('doc.hint.code', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>}
              </div>
              </PersonalizedField>
            );
            if (fld.key === 'name') return (
              <PersonalizedField componentKey="modal" fieldKey="name" label={t('doc.field.name', 'Name')}>
              <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.field.name', 'Name')}</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></div>
              </PersonalizedField>
            );
            if (fld.key === 'entity') return (
              <PersonalizedField componentKey="modal" fieldKey="entity" label={t('doc.field.primary_entity', 'Primary Entity')}>
              <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.field.primary_entity', 'Primary Entity')}</label>
                <input type="text" value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} placeholder="po_purchase_order" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></div>
              </PersonalizedField>
            );
            if (fld.key === 'pk') return (
              <PersonalizedField componentKey="modal" fieldKey="pk" label={t('doc.field.pk_column', 'PK Column')}>
              <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.field.pk_column', 'PK Column')}</label>
                <input type="text" value={form.pk} onChange={e => setForm(f => ({ ...f, pk: e.target.value }))} placeholder="po_id" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></div>
              </PersonalizedField>
            );
            if (fld.key === 'output') return (
              <PersonalizedField componentKey="modal" fieldKey="output" label={t('doc.field.default_output', 'Default Output')}>
              <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('doc.field.default_output', 'Default Output')}</label>
                <select value={form.output} onChange={e => setForm(f => ({ ...f, output: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{outputOpts.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}</select></div>
              </PersonalizedField>
            );
            if (udcForm.isUdcKey(fld.key)) return (
              <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
                readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
                {udcForm.renderControl(fld.key)}
              </PersonalizedField>
            );
            return null;
          }}
        />
        </div>

        {LOCALES.map(loc => (
          <div key={loc} className="mb-3 rounded border border-csm-border-soft p-2">
            <div className="mb-1 text-csm-xs font-semibold text-csm-ink-soft">{loc}</div>
            <input type="text" value={tl[loc]?.title ?? ''} onChange={e => setTl(prev => ({ ...prev, [loc]: { ...prev[loc], locale_code: loc, title: e.target.value } }))} placeholder={t('doc.placeholder.title', 'Title (tokens allowed)')} className="mb-1 h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
            <textarea rows={5} value={tl[loc]?.body_html ?? ''} onChange={e => setTl(prev => ({ ...prev, [loc]: { ...prev[loc], locale_code: loc, body_html: e.target.value } }))} placeholder={t('doc.placeholder.body_html', 'Body HTML — {{field}} and {{#section name}}…{{row.field}}…{{/section}}')} className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" />
          </div>
        ))}

        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('doc.active', 'Active')}</label>
          <button onClick={() => void saveTemplate()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('doc.saving', 'Saving…') : t('doc.save_template', 'Save Template')}</button>
        </div>

        {!isCreate && (
          <>
            <div className="mb-3 border-t border-csm-border pt-2">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.section.bindings', 'Related Bindings (repeating sections)')}</span>
                <button onClick={() => void addBinding()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('doc.add_binding', '+ Binding')}</button>
              </div>
              {bindings.map((b, i) => (
                <div key={b.doc_binding_id} className="mb-1 flex flex-wrap items-center gap-1">
                  <input value={b.binding_name} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, binding_name: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('doc.placeholder.section_name', 'section name')} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={b.related_entity} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, related_entity: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('doc.placeholder.child_table', 'child table')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={b.fk_column} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, fk_column: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('doc.placeholder.fk_column', 'fk column')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input value={b.order_by ?? ''} onChange={e => setBindings(bs => bs.map((x, j) => j === i ? { ...x, order_by: e.target.value } : x))} onBlur={() => void saveBinding(bindings[i])} placeholder={t('doc.placeholder.order_by', 'order by')} className="h-7 w-24 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <button onClick={() => void delBinding(b.doc_binding_id)} className="text-csm-danger text-csm-xs">{t('doc.action.delete_binding', 'delete')}</button>
                </div>
              ))}
            </div>

            <div className="border-t border-csm-border pt-2">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.section.test_generate', 'Test Generate')}</div>
              <div className="flex items-center gap-2">
                <input value={testId} onChange={e => setTestId(e.target.value)} placeholder={t('doc.placeholder.record_id', 'record id')} className="h-8 w-40 rounded border border-csm-border px-2 font-mono text-csm-sm" />
                <button onClick={() => void testGenerate()} disabled={busy} className="h-8 rounded border border-csm-border bg-white px-3 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('doc.generate', 'Generate')}</button>
                {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              </div>
            </div>
          </>
        )}

        {error &&<div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('doc.hint.save_first', 'Save the template first to add bindings and test generation.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('doc.discard.title', 'Discard changes?')}
          message={t('doc.discard.message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('doc.discard.confirm', 'Discard')}
          cancelLabel={t('doc.discard.cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(initialValues); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
